import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Card,
  CardContent,
  CardActions,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import SecurityIcon from '@mui/icons-material/Security';

const API_BASE = '';

function SettingsPage() {
  const [gmailStatus, setGmailStatus] = useState({
    loading: true,
    authenticated: false,
    email: null,
    error: null
  });
  const [actionLoading, setActionLoading] = useState(false);

  // Check Gmail authentication status
  const checkGmailStatus = useCallback(async () => {
    setGmailStatus(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch(`${API_BASE}/api/gmail/auth-status`);
      const data = await response.json();
      setGmailStatus({
        loading: false,
        authenticated: data.authenticated,
        email: data.email,
        error: data.error || null
      });
    } catch (error) {
      setGmailStatus({
        loading: false,
        authenticated: false,
        email: null,
        error: error.message
      });
    }
  }, []);

  useEffect(() => {
    checkGmailStatus();

    // Listen for OAuth callback messages
    const handleMessage = (event) => {
      if (event.data?.type === 'GMAIL_AUTH_SUCCESS') {
        checkGmailStatus();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [checkGmailStatus]);

  // Start Gmail OAuth authorization
  const handleGmailConnect = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/gmail/auth/url`);
      const data = await response.json();

      if (data.success && data.authUrl) {
        // Open authorization window
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        window.open(
          data.authUrl,
          'Gmail OAuth',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
        );
      } else {
        throw new Error(data.error || 'Unable to get authorization URL');
      }
    } catch (error) {
      setGmailStatus(prev => ({ ...prev, error: error.message }));
    } finally {
      setActionLoading(false);
    }
  };

  // Disconnect Gmail binding
  const handleGmailDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Gmail? This will disable automatic OTP reading.')) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/gmail/auth/logout`, {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        setGmailStatus({
          loading: false,
          authenticated: false,
          email: null,
          error: null
        });
      } else {
        throw new Error(data.error || 'Failed to disconnect');
      }
    } catch (error) {
      setGmailStatus(prev => ({ ...prev, error: error.message }));
    } finally {
      setActionLoading(false);
    }
  };

  // Test OTP reading
  const handleTestOtp = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/gmail/test-otp?minutes=10`);
      const data = await response.json();

      if (data.success) {
        alert(`OTP found: ${data.otp}\n\nEmail snippet: ${data.snippet}`);
      } else {
        alert(`OTP not found: ${data.error}`);
      }
    } catch (error) {
      alert(`Test failed: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
        <SecurityIcon color="primary" />
        Settings
      </Typography>

      {/* Gmail OAuth Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <EmailIcon color="error" />
            <Typography variant="h6">Gmail OAuth Binding</Typography>
            <Tooltip title="Refresh status">
              <IconButton size="small" onClick={checkGmailStatus} disabled={gmailStatus.loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Bind Gmail to automatically read Amazon Vendor Central OTP verification codes.
            This enables automated login without manual OTP entry.
          </Typography>

          <Divider sx={{ my: 2 }} />

          {gmailStatus.loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={24} />
              <Typography>Checking authentication status...</Typography>
            </Box>
          ) : gmailStatus.authenticated ? (
            <Stack spacing={2}>
              <Alert
                severity="success"
                icon={<CheckCircleIcon />}
                sx={{ alignItems: 'center' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography>Gmail connected:</Typography>
                  <Chip
                    label={gmailStatus.email}
                    color="success"
                    size="small"
                    icon={<EmailIcon />}
                  />
                </Box>
              </Alert>

              <Typography variant="body2" color="text.secondary">
                The system can now automatically read Amazon OTP emails from this Gmail account.
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Alert severity="warning" icon={<ErrorIcon />}>
                Gmail not connected. Auto OTP reading is disabled.
              </Alert>

              {gmailStatus.error && (
                <Alert severity="error">
                  Error: {gmailStatus.error}
                </Alert>
              )}

              <Typography variant="body2" color="text.secondary">
                Click the button below to authorize Gmail access. A popup window will open for Google authentication.
              </Typography>
            </Stack>
          )}
        </CardContent>

        <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
          {gmailStatus.authenticated ? (
            <>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={handleTestOtp}
                disabled={actionLoading}
              >
                Test OTP Reading
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<LinkOffIcon />}
                onClick={handleGmailDisconnect}
                disabled={actionLoading}
              >
                Disconnect
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <LinkIcon />}
              onClick={handleGmailConnect}
              disabled={actionLoading || gmailStatus.loading}
              sx={{
                background: 'linear-gradient(45deg, #EA4335 30%, #FBBC05 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #D33426 30%, #E8AC04 90%)',
                }
              }}
            >
              {actionLoading ? 'Connecting...' : 'Connect Gmail'}
            </Button>
          )}
        </CardActions>
      </Card>

      {/* Amazon Vendor Central Section */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <img
              src="https://www.amazon.com/favicon.ico"
              alt="Amazon"
              style={{ width: 20, height: 20 }}
            />
            <Typography variant="h6">Amazon Vendor Central</Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Vendor Central login credentials for Brand Analytics data scraping.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: 80 }}>
                Account:
              </Typography>
              <Chip label="bl1@capbarbell.com" size="small" variant="outlined" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: 80 }}>
                Market:
              </Typography>
              <Chip label="US" size="small" color="primary" />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Paper sx={{ mt: 3, p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          How Auto Login Works:
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>System logs into Amazon Vendor Central with saved credentials</li>
            <li>Amazon sends OTP verification email</li>
            <li>System reads OTP from Gmail (requires Gmail binding)</li>
            <li>OTP is entered automatically to complete login</li>
            <li>Session is maintained for Search Terms scraping</li>
          </ol>
        </Typography>
      </Paper>
    </Box>
  );
}

export default SettingsPage;
